import { useContext, useState } from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { getRxFilePreviewUrl, TaskEvent, TaskComment, OutboundTaskTypeKey, OutboundTaskType } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import DeclineModal from '../../DeclineModal';
import SubmitAppealModal from '../../SubmitAppealModal';
import AppealEditModal from '../../AppealEditModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { appealGenerationNotice } from '../../../businessRelationshipNotices/copy';
import { getBenefitTypeLabel } from '../../utils';
import { formatDrugName } from 'shared-utils/utils/dropdownFilter';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { FloatingEditButton } from '../../../FloatingEditButton/FloatingEditButton';
import {
  FormActionContainer,
  StyledIframe,
  StickyButtonContainer,
  FLOATING_BUTTON_TOP_OFFSET,
  FLOATING_BUTTON_RIGHT_OFFSET,
} from '../../actions/shared/styles';
import { UploadedFileRow, AttachedFile } from '../../Files';
import { PharmaPilotDrug } from '../../../../types';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

const StyledFormActionContainer = styled(FormActionContainer)`
  padding-bottom: 0;
`;

type ModalState = 'decline' | 'submit_appeal' | 'edit_appeal' | 'other_request';

/**
 * Dedicated task view for Appeal Review (appeal drafted, ready for review).
 */
const AppealReviewTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [modalState, setModalState] = useState<{ show: boolean; type: ModalState }>({
    show: false,
    type: 'decline',
  });
  const [documentId, setDocumentId] = useState<string | null>(taskResponse.task.document_id ?? null);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;

  const closeModal = () => setModalState((prev) => ({ ...prev, show: false }));

  const extraData: { label: string; value: string }[] = [];
  const benefitTypeLabel = getBenefitTypeLabel(task);
  if (benefitTypeLabel) {
    extraData.push({ label: 'Benefit type', value: benefitTypeLabel });
  }

  const isCompleted = task.status === 'completed';

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const lastTimelineItem = task.timeline[task.timeline.length - 1];
      if (lastTimelineItem?.item_type === 'event') {
        completionEvent = lastTimelineItem as TaskEvent;
      }
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for Appeal Review task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const firstComment =
    task.timeline && task.timeline.length > 0 && task.timeline[0].item_type === 'comment'
      ? (task.timeline[0] as TaskComment)
      : undefined;
  const denialLetterAttachmentIds = firstComment?.attachment_document_ids ?? [];

  let modalToShow = null;
  if (modalState.show) {
    switch (modalState.type) {
      case 'decline':
        modalToShow = (
          <DeclineModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            taskKey={task.task_key as OutboundTaskTypeKey}
            taskId={taskId}
            onTaskUpdate={onTaskUpdate}
          />
        );
        break;
      case 'submit_appeal':
        modalToShow = (
          <SubmitAppealModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            taskId={taskId}
            includeEnrollmentNotice={false}
          />
        );
        break;
      case 'edit_appeal':
        modalToShow = documentId ? (
          <AppealEditModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            documentId={documentId}
            setDocumentId={setDocumentId}
            taskId={taskId}
          />
        ) : null;
        break;
      case 'other_request':
        modalToShow = (
          <NewFollowupTaskModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            patientId={patient.id}
            prescriptionId={drug.id}
            taskId={taskId}
            relatedTaskKey={task.task_key as OutboundTaskTypeKey}
            relatedTaskType={task.type as OutboundTaskType}
          />
        );
        break;
    }
  }

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
          extraData,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: true, // APPEAL_REVIEW always shows
            businessRelationshipNoticeCopy: appealGenerationNotice,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        <div className="mb-4 flex flex-col gap-3">
          <TaskInstructionSummary>
            Please review the draft appeal letter and select an option below. The original denial letter is attached for
            reference.
          </TaskInstructionSummary>
          {task.pilot_drug_name === PharmaPilotDrug.TREMFYA && (
            <InfoBox variant="success" noTopMargin noBottomMargin>
              <Typography styledAs="bodySmallDMSans">
                Most appeals for {formatDrugName(task.pilot_drug_name)} are successful in overturning denials, based on
                Tandem data.
              </Typography>
            </InfoBox>
          )}
          {denialLetterAttachmentIds.length > 0 && (
            <UploadedFileRow>
              {denialLetterAttachmentIds.map((docId) => (
                <AttachedFile key={docId} fileId={docId} />
              ))}
            </UploadedFileRow>
          )}
        </div>

        {!isCompleted && documentId && (
          <StyledFormActionContainer>
            <StyledIframe src={getRxFilePreviewUrl(drug.id, documentId)} />
            <StickyButtonContainer>
              <FloatingEditButton
                top={FLOATING_BUTTON_TOP_OFFSET}
                right={FLOATING_BUTTON_RIGHT_OFFSET}
                onClick={() => setModalState({ show: true, type: 'edit_appeal' })}
              />
              <ButtonRow align={Alignment.SpaceBetween}>
                <ButtonRow align={Alignment.Left}>
                  <Button variant="tertiary" size="sm" onClick={() => setModalState({ show: true, type: 'decline' })}>
                    Cancel prescription
                  </Button>
                  <OtherRequestButton
                    setShowNewFollowupTaskModal={(show) => setModalState({ show, type: 'other_request' })}
                  />
                </ButtonRow>
                <Button size="sm" onClick={() => setModalState({ show: true, type: 'submit_appeal' })}>
                  Approve and submit
                </Button>
              </ButtonRow>
            </StickyButtonContainer>
          </StyledFormActionContainer>
        )}
      </StructuredTaskViewWrapper>

      {modalState.show && modalToShow}
    </>
  );
};

export default AppealReviewTaskView;
